/**
 * Secure console utilities.
 *
 * Overrides console.error in production builds to avoid leaking
 * stack traces or sensitive payload details while keeping verbose
 * logging available for local development.
 */

const GLOBAL_KEY = "__IPHUB_SECURE_CONSOLE__";

function initializeSecureConsole() {
  const root = getRoot();

  if (!root || !root.console || root[GLOBAL_KEY]) {
    return;
  }

  const originalError = typeof root.console.error === "function"
    ? root.console.error.bind(root.console)
    : null;

  if (!originalError) {
    root[GLOBAL_KEY] = { originalError: null };
    return;
  }

  root.console.error = (...args) => {
    if (process.env.NODE_ENV !== "production") {
      originalError(...args);
      return;
    }

    const sanitizedArgs = sanitizeErrorArgs(args);
    originalError(...sanitizedArgs);
  };

  root[GLOBAL_KEY] = { originalError };
}

function sanitizeErrorArgs(args) {
  if (!Array.isArray(args) || args.length === 0) {
    return ["Operation failed."];
  }

  if (process.env.NODE_ENV !== "production") {
    return args;
  }

  for (const candidate of args) {
    if (candidate instanceof Error && typeof candidate.message === "string") {
      return [candidate.message];
    }
  }

  const [first] = args;
  if (typeof first === "string" && first.trim().length > 0) {
    return [first];
  }

  return ["Operation failed."];
}

initializeSecureConsole();

/**
 * Helper function that callers can use for explicit error logging.
 * Automatically respects the production sanitization rules.
 */
export function logError(message, error) {
  const logger = getErrorLogger();

  if (process.env.NODE_ENV !== "production") {
    if (error !== undefined) {
      logger(message, error);
    } else {
      logger(message);
    }
    return;
  }

  if (typeof message === "string" && message.trim().length > 0) {
    logger(message);
    return;
  }

  if (error instanceof Error && typeof error.message === "string") {
    logger(error.message);
    return;
  }

  logger("Operation failed.");
}

function getErrorLogger() {
  const root = getRoot();

  if (root?.console && typeof root.console.error === "function") {
    return root.console.error.bind(root.console);
  }

  const metadata = root?.[GLOBAL_KEY];
  if (metadata && typeof metadata.originalError === "function") {
    return metadata.originalError;
  }

  return () => {};
}

function getRoot() {
  if (typeof window !== "undefined") {
    return window;
  }

  if (typeof globalThis !== "undefined") {
    return globalThis;
  }

  return undefined;
}
